import { RouteSettlementRecord } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiAsyncConfirmationDialog, NoddiSheet, useAppForm, useConfirmedSubmit } from 'noddi-ui-web';
import { useEffect } from 'react';
import { z } from 'zod';

const FORM_ID = 'edit-route-settlement-form';

const validationSchema = z.object({
  isVehicleCompensated: z.boolean(),
  extraPayment: z.number(),
  extraPaymentDescription: z.string()
});

type FormValues = z.input<typeof validationSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  routeSettlement: RouteSettlementRecord;
};

export const EditRouteSettlementSheet = ({ open, onClose, routeSettlement }: Props) => {
  const { mutateAsync: patchRouteSettlement } = noddiAsync.usePatch({
    type: URLKeys.patchRouteSettlement,
    queryConfig: {
      onSuccess: async () => {
        invalidateQueryExactMatch({
          urlKey: URLKeys.getRouteSettlement,
          input: { routeSettlementId: routeSettlement.id }
        });
      }
    }
  });

  const getDefaultValues = (): FormValues => ({
    isVehicleCompensated: routeSettlement.isVehicleCompensated,
    extraPayment: routeSettlement.extraPayment.amount,
    extraPaymentDescription: routeSettlement.extraPaymentDescription
  });

  const save = async (value: FormValues) => {
    await patchRouteSettlement({ routeSettlementId: routeSettlement.id, ...value }, { onSuccess: onClose });
  };

  // A pay change asks first; a description-only edit saves straight away.
  const confirmedSubmit = useConfirmedSubmit({
    save,
    needsConfirmation: (value: FormValues) => {
      const saved = getDefaultValues();
      return value.isVehicleCompensated !== saved.isVehicleCompensated || value.extraPayment !== saved.extraPayment;
    }
  });

  const form = useAppForm({
    defaultValues: getDefaultValues(),
    validators: { onSubmit: validationSchema },
    onSubmit: ({ value }) => confirmedSubmit.onSubmit(value)
  });

  useEffect(() => {
    if (open) {
      form.reset(getDefaultValues());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getDefaultValues is a new closure each render and would reset on every render; reset only on open or when the fields it reads change
  }, [
    open,
    form,
    routeSettlement.isVehicleCompensated,
    routeSettlement.extraPayment.amount,
    routeSettlement.extraPaymentDescription
  ]);

  return (
    <form.AppForm>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title='Edit settlement'
        description='Extra payment adjusts route pay for freelancers and subcontractors. Vehicle compensation controls whether eligible mileage and tolls can be included.'
        footer={<form.SubscribeButton formId={FORM_ID} label='Update settlement' className='w-full' />}
      >
        <form
          id={FORM_ID}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className='flex flex-col gap-5'>
            <form.AppField name='isVehicleCompensated'>
              {(field) => <field.Switch label='Is vehicle compensated' />}
            </form.AppField>
            <form.AppField name='extraPayment'>
              {(field) => <field.Input label='Extra payment' type='number' />}
            </form.AppField>
            <form.AppField name='extraPaymentDescription'>
              {(field) => <field.Input label='Extra payment description' />}
            </form.AppField>
          </div>
        </form>
      </NoddiSheet>
      <NoddiAsyncConfirmationDialog
        {...confirmedSubmit.confirmationDialogProps}
        title='Change what this route pays?'
        description='Extra payment changes route pay for freelancers and subcontractors. Turning vehicle compensation off removes mileage and tolls for this route. Turning it on still requires an eligible worker and vehicle.'
        confirmText='Update settlement'
      />
    </form.AppForm>
  );
};
